/**
 * Framework-free carousel: the replacement for Swiper in astro-ui's sliders, and (through
 * composables/useCarousel) in the Vue ones.
 *
 * Layout lives in CSS (../assets/styles/carousel.css). The track's transform is a calc() over
 * custom properties — slides per view, gap, index — so the server can render the start
 * position (`--i` inline) and the first paint is already correct, with no JS and no
 * flash. This module only moves `--i`, adds pointer dragging, and tags the slides with
 * the same states Swiper did (`is-active`, `is-prev`, `is-next`) so templates can style
 * them with `group-[.is-active]/slide:` variants.
 *
 * Markup contract:
 *   <div class="dr-carousel [dr-carousel--centered]" style="--i: 0">
 *     <div class="dr-carousel-track" data-carousel-track>
 *       <div class="dr-carousel-slide group/slide" data-carousel-slide>…</div>
 *     </div>
 *   </div>
 *
 * The math mirrors Swiper 11's `updateSlides` for equal-width slides: a slide is
 * `(width - (perView - 1) * gap) / perView`; centred mode puts slide `i`'s centre on the
 * container's; otherwise the strip stops once its end reaches the container's edge, and
 * that end is one last stop of its own (Swiper's extra snap), so the index can run one
 * past the last whole-slide position.
 */

export interface CarouselChange {
  /** Active slide (a clone's index, in a cloned strip — see `loopPeriod`). */
  index: number
  /** Highest reachable index. */
  maxIndex: number
}

export interface CarouselOptions {
  /** Start index. Defaults to the root's server-rendered `--i`. */
  index?: number
  /**
   * For a strip rendered as repeated copies of `loopPeriod` cards: once a move settles
   * outside the middle copy, jump silently to the same card in the
   * middle copy, so the strip never runs out.
   */
  loopPeriod?: number
  /** Pointer/touch dragging. Swiper's `allowTouchMove`. Default true. */
  draggable?: boolean
  /** Transition for a drag release, in ms. Swiper's default `speed`. */
  speed?: number
  onChange?: (change: CarouselChange) => void
}

export interface Carousel {
  readonly index: number
  readonly maxIndex: number
  slideTo(index: number, speed?: number): void
  next(speed?: number): void
  prev(speed?: number): void
  /** Re-measure (slide count, bounds). Called on resize automatically. */
  update(): void
  destroy(): void
}

const STATES = ['is-active', 'is-prev', 'is-next'] as const

/**
 * A slide rendered with deferred images (UniversalPicture `defer`) keeps its URLs in
 * data-srcset / data-src: a lazy image in a horizontal strip is close enough to the viewport
 * that the browser fetches it on load anyway.
 */
export function loadDeferredImages(slide: Element | undefined): void {
  slide
    ?.querySelectorAll<HTMLElement>('picture > [data-srcset], picture > img[data-src]')
    .forEach(el => {
      if (el.dataset.srcset) el.setAttribute('srcset', el.dataset.srcset)
      if (el.dataset.src) el.setAttribute('src', el.dataset.src)
      delete el.dataset.srcset
      delete el.dataset.src
    })
}
/**
 * The slides of a looped strip (`loopPeriod: items.length`): three copies, of which only
 * the middle one is real; render the others `inert` and `aria-hidden`. A single item or an
 * unlooped strip is just the items. `key` is unique across the copies.
 */
export function loopCopies<T>(
  items: T[],
  loop: boolean,
): { item: T; clone: boolean; key: string }[] {
  const copies = loop && items.length > 1 ? [0, 1, 2] : [1]
  return copies.flatMap(copy =>
    items.map((item, i) => ({ item, clone: copy !== 1, key: `${copy}-${i}` })),
  )
}

const DRAG_START_PX = 6

const reducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

export function createCarousel(root: HTMLElement, options: CarouselOptions = {}): Carousel {
  const track = root.querySelector<HTMLElement>('[data-carousel-track]')
  if (!track) throw new Error('createCarousel: no [data-carousel-track] inside the root')

  const centered = root.classList.contains('dr-carousel--centered')
  const releaseSpeed = options.speed ?? 300
  const allSlides = Array.from(
    track.querySelectorAll<HTMLElement>(':scope > [data-carousel-slide]'),
  )

  let index = options.index ?? (Number(root.style.getPropertyValue('--i')) || 0)
  let slides = allSlides
  let maxIndex = 0
  let settleTimer: ReturnType<typeof setTimeout> | undefined

  const setSpeed = (ms: number) => root.style.setProperty('--speed', `${ms}ms`)

  const render = () => {
    root.style.setProperty('--i', String(index))
    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index)
      slide.classList.toggle('is-prev', i === index - 1)
      slide.classList.toggle('is-next', i === index + 1)
    })
    options.onChange?.({ index, maxIndex })
  }

  const measure = () => {
    // Swiper skips `display: none` slides (e.g. a desktop-only spacer), so do we.
    slides = allSlides.filter(s => getComputedStyle(s).display !== 'none')
    allSlides.forEach(s => !slides.includes(s) && s.classList.remove(...STATES))
    root.style.setProperty('--n', String(slides.length))
    if (centered || slides.length < 2) {
      maxIndex = Math.max(0, slides.length - 1)
      return
    }
    // Sub-pixel rects, not the rounded offset* sizes: at a fractional track width their
    // rounding, multiplied by the stop count, adds a phantom last stop. The track's
    // transform shifts every rect alike, so the differences are unaffected.
    // At least two slides past the early return above.
    const first = slides[0]!.getBoundingClientRect()
    const step = slides[1]!.getBoundingClientRect().left - first.left
    const overflow =
      slides[slides.length - 1]!.getBoundingClientRect().right -
      first.left -
      track.getBoundingClientRect().width
    if (step <= 0 || overflow <= 0) {
      maxIndex = 0
      return
    }
    const whole = Math.floor(overflow / step + 1e-3)
    maxIndex = overflow - whole * step > 1 ? whole + 1 : whole
  }

  const clamp = (i: number) => Math.min(Math.max(i, 0), maxIndex)

  // A cloned strip hops back to the middle copy after every move that leaves it, so the
  // reader never meets its end and the active card is always a real one (the copies are
  // hidden from assistive tech). Instant: the copy it lands on looks identical.
  const recentre = () => {
    const period = options.loopPeriod
    if (!period || dragging) return
    const middle = Math.floor(slides.length / period / 2) * period
    const target = middle + (((index % period) + period) % period)
    if (target === index) return
    setSpeed(0)
    index = target
    render()
    void track.offsetWidth // commit the jump before transitions come back
    setSpeed(releaseSpeed)
  }

  const slideTo = (target: number, speed = releaseSpeed) => {
    // Re-measured on every move, not just on resize: a stylesheet that lands after the
    // script (a breakpoint-only slide appearing) changes the stops without resizing.
    measure()
    const next = clamp(Math.round(target))
    for (let i = next - 1; i <= next + 1; i++) loadDeferredImages(slides[i])
    const ms = reducedMotion() ? 0 : speed
    setSpeed(ms)
    root.style.setProperty('--drag', '0px')
    const moved = next !== index
    index = next
    render()
    clearTimeout(settleTimer)
    // transitionend never fires for a zero-length or no-op move, so settle on a timer
    // that covers both (and a tab that was hidden mid-transition).
    settleTimer = setTimeout(recentre, moved ? ms + 20 : 0)
  }

  // --- dragging -------------------------------------------------------------------
  // Listeners sit on the root, not the track, as Swiper's sit on its container: a drag
  // that starts in the padding around the cards (where the arrows are) still moves them.
  let pointerId: number | null = null
  let startX = 0
  let startY = 0
  let startTime = 0
  let dx = 0
  let base = 0
  let dragging = false

  const translateX = () => {
    const transform = getComputedStyle(track).transform
    return typeof DOMMatrix === 'function' && transform && transform !== 'none'
      ? new DOMMatrix(transform).m41
      : 0
  }

  const stepSize = () =>
    slides.length > 1
      ? slides[1]!.getBoundingClientRect().left - slides[0]!.getBoundingClientRect().left
      : track.getBoundingClientRect().width

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // As Swiper does: a mouse press on a card must not start the browser's own drag of
    // the link or image under it, which would swallow every pointer event after it.
    if (
      e.pointerType === 'mouse' &&
      !(e.target as Element).closest?.('input, textarea, select, [contenteditable]')
    ) {
      e.preventDefault()
    }
    pointerId = e.pointerId
    startX = e.clientX
    startY = e.clientY
    startTime = performance.now()
    dx = 0
    base = 0
    dragging = false
    // Moves and the release are followed on the document, as Swiper does: the pointer
    // usually leaves the slider's box before a drag has started.
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', endDrag)
    document.addEventListener('pointercancel', endDrag)
  }

  const stopFollowing = () => {
    document.removeEventListener('pointermove', onPointerMove)
    document.removeEventListener('pointerup', endDrag)
    document.removeEventListener('pointercancel', endDrag)
  }

  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return
    const x = e.clientX - startX
    if (!dragging) {
      if (Math.abs(x) < DRAG_START_PX) return
      // A mostly-vertical gesture is a page scroll (touch-action: pan-y hands those to
      // the browser, which cancels the pointer); for a mouse, just ignore it.
      if (Math.abs(e.clientY - startY) > Math.abs(x)) {
        pointerId = null
        stopFollowing()
        return
      }
      dragging = true
      root.setPointerCapture?.(e.pointerId)
      // A move still settling must not hop or re-enable transitions under the finger,
      // and a strip caught mid-animation continues from where it visibly is (Swiper
      // reads the current translate the same way).
      clearTimeout(settleTimer)
      const before = translateX()
      setSpeed(0)
      base = before - translateX()
      root.classList.add('is-dragging')
    }
    // Resist past either end, as Swiper does.
    const pastStart = index === 0 && x > 0
    const pastEnd = index === maxIndex && x < 0
    dx = base + (pastStart || pastEnd ? x * 0.3 : x)
    root.style.setProperty('--drag', `${dx}px`)
  }

  const endDrag = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return
    pointerId = null
    stopFollowing()
    if (!dragging) return
    dragging = false
    root.classList.remove('is-dragging')
    // Swiper's release rule: a quick swipe (under 300ms) always reaches the next slide
    // in its direction; a slower drag settles on whichever slide is nearest, so it has
    // to pass half a slide to move at all.
    const step = stepSize()
    const slides = step ? -dx / step : 0
    const quick = performance.now() - startTime < 300
    const delta = quick ? Math.sign(slides) * Math.ceil(Math.abs(slides)) : Math.round(slides)
    slideTo(index + delta)
    // The drag must not also count as a click on the link under the pointer.
    root.addEventListener('click', swallowClick, { capture: true, once: true })
    setTimeout(() => root.removeEventListener('click', swallowClick, { capture: true }), 0)
  }

  // Tabbing to a card the strip isn't showing makes the browser scroll the overflow: hidden
  // root to it, an offset the transform knows nothing about: undo it and slide there instead.
  const resetScroll = () => {
    if (root.scrollLeft) root.scrollLeft = 0
  }
  const onFocusIn = (e: FocusEvent) => {
    resetScroll()
    const slide = (e.target as Element).closest?.('[data-carousel-slide]') as HTMLElement | null
    const i = slide ? slides.indexOf(slide) : -1
    if (i < 0 || i === index) return
    // Only a card the strip isn't showing: a click on a visible one (no drag to stop it in a
    // non-draggable carousel) also focuses it, and the strip must not move under the pointer.
    const box = root.getBoundingClientRect()
    const card = slide!.getBoundingClientRect()
    if (card.left >= box.left - 1 && card.right <= box.right + 1) return
    slideTo(i)
  }
  root.addEventListener('focusin', onFocusIn)
  root.addEventListener('scroll', resetScroll)

  const swallowClick = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }
  const preventNativeDrag = (e: Event) => e.preventDefault()
  // Someone pointing at (pointerover also fires for a mouse already there) or tabbing into the strip is about to move it: fetch the next slide.
  const warmNext = () => loadDeferredImages(slides[index + 1])
  root.addEventListener('pointerover', warmNext, { once: true })
  root.addEventListener('focusin', warmNext, { once: true })

  if (options.draggable !== false) {
    root.classList.add('is-draggable')
    root.addEventListener('pointerdown', onPointerDown)
    root.addEventListener('dragstart', preventNativeDrag)
  }

  const resizeObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          measure()
          const clamped = clamp(index)
          if (clamped !== index) slideTo(clamped, 0)
          else render()
        })
      : undefined
  resizeObserver?.observe(root)

  setSpeed(releaseSpeed)
  measure()
  index = clamp(index)
  render()
  root.classList.add('is-ready')

  return {
    get index() {
      return index
    },
    get maxIndex() {
      return maxIndex
    },
    slideTo,
    next: speed => slideTo(index + 1, speed),
    prev: speed => slideTo(index - 1, speed),
    update: () => {
      measure()
      slideTo(index, 0)
    },
    destroy: () => {
      clearTimeout(settleTimer)
      resizeObserver?.disconnect()
      stopFollowing()
      root.removeEventListener('pointerdown', onPointerDown)
      root.removeEventListener('dragstart', preventNativeDrag)
      root.removeEventListener('pointerover', warmNext)
      root.removeEventListener('focusin', warmNext)
      root.removeEventListener('focusin', onFocusIn)
      root.removeEventListener('scroll', resetScroll)
    },
  }
}
